import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { appointmentApi } from '@/api/appointment';
import { Appointment } from '@/types/types';
import { defaultOpdPayoutServiceMonth } from '@/lib/opdPayout';

type Props = {
  appointment: Appointment | null;
  open: boolean;
  onClose: () => void;
};

const PAYMENT_MODES = ['CASH', 'UPI', 'CARD', 'BANK_TRANSFER', 'CHEQUE'] as const;

export default function OpdPayoutDialog({ appointment, open, onClose }: Props) {
  const queryClient = useQueryClient();
  const appointmentId = appointment?.id ?? '';

  const { data: contextRes, isLoading: contextLoading } = useQuery({
    queryKey: ['appointment-opd-payout', appointmentId],
    enabled: open && !!appointmentId,
    queryFn: () => appointmentApi.getOpdPayoutContext(appointmentId),
  });

  const context = contextRes?.data?.data;

  const [amount, setAmount] = useState('');
  const [serviceMonth, setServiceMonth] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMode, setPaymentMode] = useState('');

  useEffect(() => {
    if (!open || !appointment) return;
    setServiceMonth(defaultOpdPayoutServiceMonth(appointment));
    setAmount('');
    setDescription('');
    setPaymentMode('');
  }, [open, appointment]);

  useEffect(() => {
    if (context?.serviceMonth && !serviceMonth) {
      setServiceMonth(context.serviceMonth);
    }
  }, [context?.serviceMonth, serviceMonth]);

  const createMut = useMutation({
    mutationFn: () =>
      appointmentApi.createOpdPayout(appointmentId, {
        amount: Number(amount),
        serviceMonth: serviceMonth || undefined,
        description: description.trim() || undefined,
        paymentMode: paymentMode.trim() || undefined,
      }),
    onSuccess: (res) => {
      const expNo = res.data?.data?.expenseNumber;
      toast.success(
        expNo
          ? `OPD payout recorded (${expNo}) — visible under Expenses → OPD payout`
          : 'OPD payout recorded — visible under Expenses → OPD payout'
      );
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Could not record OPD payout');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Enter a valid payout amount');
      return;
    }
    createMut.mutate();
  };

  const doctorName = context?.doctor?.name ?? appointment?.doctor?.name ?? 'Doctor';
  const visitLabel = appointment?.visitId ? ` · ${appointment.visitId}` : '';
  const blocked = context && !context.eligible;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record OPD payout</DialogTitle>
        </DialogHeader>

        {contextLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : blocked ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            {context.reason ?? 'This visit is not eligible for OPD payout.'}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
              <p>
                <span className="text-muted-foreground">Doctor:</span> {doctorName}
              </p>
              <p>
                <span className="text-muted-foreground">Patient:</span>{' '}
                {appointment?.patient?.name ?? '—'}
                {visitLabel}
              </p>
              {context?.opdConsultNet != null && context.opdConsultNet > 0 && (
                <p>
                  <span className="text-muted-foreground">OPD consult on bill:</span> ₹
                  {context.opdConsultNet.toLocaleString('en-IN')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="opd-payout-amount">Payout amount (₹)</Label>
              <Input
                id="opd-payout-amount"
                type="number"
                min={0}
                step="0.01"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter doctor payout"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="opd-payout-month">Service month</Label>
              <Input
                id="opd-payout-month"
                type="month"
                value={serviceMonth}
                onChange={(e) => setServiceMonth(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="opd-payout-desc">Note (optional)</Label>
              <Input
                id="opd-payout-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional note for finance"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="opd-payout-mode">Payment mode (optional)</Label>
              <select
                id="opd-payout-mode"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <option value="">Save as unpaid / draft</option>
                {PAYMENT_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                If you pick a payment mode, the expense is marked paid immediately (same as Expenses
                module).
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save OPD payout'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {blocked && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
